import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import munilexLogo from '@/assets/munilex-logo.png';
import { NewChatButton } from './NewChatButton';
import { SearchChatsInput } from './SearchChatsInput';
import { PinnedChatsSection } from './PinnedChatsSection';
import { FolderList } from './FolderList';
import { FolderDialog } from './FolderDialog';
import { DeleteFolderDialog } from './DeleteFolderDialog';
import { UpgradePlanButton } from './UpgradePlanButton';
import { UserMenu } from './UserMenu';
import { PlanBadge } from './PlanBadge';
import { SettingsModal } from '@/components/settings/SettingsModal';
import { EditTitleDialog } from '@/components/chat/EditTitleDialog';
import { DeleteChatDialog } from '@/components/chat/DeleteChatDialog';
import { AdvancedFilters, getDefaultFilters } from './AdvancedSearchFilters';
import { DeadlineTriggerButton } from '@/components/deadlines/DeadlineTriggerButton';
import { ModeSwitcher } from './ModeSwitcher';
import { NotificationCenter } from '@/components/notifications/NotificationCenter';
import { useAuth } from '@/hooks/useAuth';
import { useEffectiveMode } from '@/hooks/useEffectiveMode';
import { listChatsByUser, createChat, updateChatTitle, deleteChat, togglePinChat, advancedSearchChats, Chat, SearchFilters, ChatMode } from '@/services/chatService';
import { appModeToChatMode, getChatUrl as buildChatUrl } from '@/lib/chatModeRoutes';
import { listFolders, createFolder, updateFolder, deleteFolder as deleteFolderService, initializeDefaultFolders, ChatFolder } from '@/services/folderService';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { LayoutDashboard, HelpCircle, FileText, Star, PenTool, Scale } from 'lucide-react';
interface SidebarProps {
  variant?: 'funcionarios' | 'oposiciones';
}
export function Sidebar({
  variant = 'funcionarios'
}: SidebarProps) {
  const navigate = useNavigate();
  const {
    user,
    profile,
    isLoading: authLoading
  } = useAuth();
  const {
    toast
  } = useToast();
  const [chats, setChats] = useState<Chat[]>([]);
  const [folders, setFolders] = useState<ChatFolder[]>([]);
  const [filteredChats, setFilteredChats] = useState<Chat[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilters>(getDefaultFilters());
  const [loading, setLoading] = useState(true);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Chat dialogs
  const [editingChat, setEditingChat] = useState<Chat | null>(null);
  const [deletingChat, setDeletingChat] = useState<Chat | null>(null);

  // Folder dialogs
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState<ChatFolder | null>(null);
  const [deletingFolder, setDeletingFolder] = useState<ChatFolder | null>(null);

  // Pinned chats
  const pinnedChats = useMemo(() => chats.filter((chat) => chat.is_pinned).sort((a, b) => {
    const aTime = a.pinned_at ? new Date(a.pinned_at).getTime() : 0;
    const bTime = b.pinned_at ? new Date(b.pinned_at).getTime() : 0;
    return bTime - aTime;
  }), [chats]);
  // Track if default folders have been initialized
  const foldersInitializedRef = useRef(false);

  // Get effective mode for navigation and filtering
  const mode = useEffectiveMode();
  // Map effective mode to chat_mode filter
  const chatModeFilter: ChatMode = appModeToChatMode(mode);

  useEffect(() => {
    if (user) {
      loadData();
      const cleanup = subscribeToChanges();
      return cleanup;
    }
  }, [user, chatModeFilter]);
  useEffect(() => {
    const applyFilters = async () => {
      if (!user) return;
      const hasAdvancedFilters = advancedFilters.datePreset !== 'any' || advancedFilters.folders.length > 0 || advancedFilters.searchInContent || advancedFilters.pinnedOnly;
      if (hasAdvancedFilters || searchQuery.trim()) {
        try {
          const filters: SearchFilters = {
            query: searchQuery.trim() || undefined,
            datePreset: advancedFilters.datePreset,
            dateRange: advancedFilters.customDateRange,
            folders: advancedFilters.folders.length > 0 ? advancedFilters.folders : undefined,
            searchInContent: advancedFilters.searchInContent,
            pinnedOnly: advancedFilters.pinnedOnly
          };
          const results = await advancedSearchChats(user.id, filters, chatModeFilter);
          setFilteredChats(results);
        } catch (error) {
          console.error('Error searching chats:', error);
          setFilteredChats(chats);
        }
      } else {
        // Apply only quick folder filter
        let result = chats;
        if (selectedFolderId === 'uncategorized') {
          result = result.filter((chat) => !chat.folder_id);
        } else if (selectedFolderId) {
          result = result.filter((chat) => chat.folder_id === selectedFolderId);
        }
        setFilteredChats(result);
      }
    };
    applyFilters();
  }, [searchQuery, chats, selectedFolderId, advancedFilters, user]);

  const loadData = useCallback(async () => {
    if (!user || authLoading) return;
    try {
      // Solo crear carpetas por defecto (Urbanismo, Personal, Contratación, Hacienda...)
      // para Funcionarios y Secretaría-Intervención. El resto de cuerpos de oposición
      // (C1, C2, IIPP, Guardia Civil) gestionan sus propias carpetas vacías.
      const shouldSeedDefaults =
        chatModeFilter === 'funcionarios' || chatModeFilter === 'oposiciones';
      if (shouldSeedDefaults && !foldersInitializedRef.current) {
        await initializeDefaultFolders(user.id);
        foldersInitializedRef.current = true;
      }
      const [foldersData, chatsData] = await Promise.all([listFolders(user.id), listChatsByUser(user.id, chatModeFilter)]);
      setFolders(foldersData);
      setChats(chatsData);
      setFilteredChats(chatsData);
      setIsDataLoaded(true);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, [user, chatModeFilter]);

  const subscribeToChanges = () => {
    if (!user || authLoading) return () => {};

    // Debounce realtime events to collapse multiple rapid changes into one reload
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const debouncedLoadData = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => loadData(), 300);
    };

    const chatsChannel = supabase.channel('chats-changes').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'chats',
      filter: `user_id=eq.${user.id}`
    }, () => {
      debouncedLoadData();
    }).subscribe();
    const foldersChannel = supabase.channel('folders-changes').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'chat_folders',
      filter: `user_id=eq.${user.id}`
    }, () => {
      debouncedLoadData();
    }).subscribe();
    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      supabase.removeChannel(chatsChannel);
      supabase.removeChannel(foldersChannel);
    };
  };

  
  const handleNewChat = async () => {
    if (!user) return;
    try {
      const newChat = await createChat(user.id, chatModeFilter);
      navigate(buildChatUrl(newChat.id, chatModeFilter));
      toast({
        title: 'Nueva consulta creada',
        description: 'Puedes empezar a escribir tu consulta jurídica'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo crear la consulta',
        variant: 'destructive'
      });
    }
  };
  const handleEditChat = async (newTitle: string) => {
    if (!editingChat) return;
    try {
      await updateChatTitle(editingChat.id, newTitle);
      toast({
        title: 'Título actualizado',
        description: 'El título de la conversación se ha actualizado correctamente'
      });
      loadData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo actualizar el título',
        variant: 'destructive'
      });
    }
  };
  const handleDeleteChat = async () => {
    if (!deletingChat) return;
    try {
      await deleteChat(deletingChat.id);
      toast({
        title: 'Conversación eliminada',
        description: 'La conversación se ha eliminado correctamente'
      });
      if (window.location.pathname.includes(deletingChat.id)) {
        if (mode === 'oposiciones_c1') {
          navigate('/app/oposiciones-c1/chat');
        } else if (mode === 'oposiciones') {
          navigate('/app/oposiciones/chat');
        } else {
          navigate('/app/chat');
        }
      }
      loadData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo eliminar la conversación',
        variant: 'destructive'
      });
    }
  };

  // Pin handler
  const handleTogglePin = async (chat: Chat) => {
    try {
      const newPinState = !chat.is_pinned;
      await togglePinChat(chat.id, newPinState);
      toast({
        title: newPinState ? 'Conversación fijada' : 'Conversación desfijada',
        description: newPinState ? 'La conversación ahora aparece en la sección de fijados' : 'La conversación ya no está fijada'
      });
      loadData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo cambiar el estado de fijado',
        variant: 'destructive'
      });
    }
  };

  // Folder handlers
  const handleCreateFolder = async (data: {
    name: string;
    color: string;
    icon: string;
  }) => {
    if (!user) return;
    try {
      await createFolder(user.id, data);
      toast({
        title: 'Carpeta creada',
        description: `La carpeta "${data.name}" se ha creado correctamente`
      });
      loadData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo crear la carpeta',
        variant: 'destructive'
      });
    }
  };
  const handleEditFolder = async (data: {
    name: string;
    color: string;
    icon: string;
  }) => {
    if (!editingFolder) return;
    try {
      await updateFolder(editingFolder.id, data);
      toast({
        title: 'Carpeta actualizada',
        description: 'Los cambios se han guardado correctamente'
      });
      setEditingFolder(null);
      loadData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo actualizar la carpeta',
        variant: 'destructive'
      });
    }
  };
  const handleDeleteFolder = async () => {
    if (!deletingFolder) return;
    try {
      await deleteFolderService(deletingFolder.id);
      toast({
        title: 'Carpeta eliminada',
        description: 'Las conversaciones se han movido a "Sin categoría"'
      });
      setDeletingFolder(null);
      loadData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo eliminar la carpeta',
        variant: 'destructive'
      });
    }
  };
  return <>
      <aside data-tour="sidebar" className={`w-[280px] ${variant === 'oposiciones' ? 'h-full' : 'h-screen'} bg-sidebar border-r border-sidebar-border flex flex-col shadow-sm`}>
        {/* Logo + Notifications */}
        






        {/* New Chat Button */}
        <div className="p-4">
          <NewChatButton onClick={handleNewChat} disabled={loading} variant={variant} />
        </div>

        {/* Search */}
        <div className="px-4 pb-4">
          <SearchChatsInput value={searchQuery} onChange={setSearchQuery} folders={folders} selectedFolderId={selectedFolderId} onFolderChange={setSelectedFolderId} advancedFilters={advancedFilters} onAdvancedFiltersChange={setAdvancedFilters} />
        </div>

        {/* Pinned Chats + Folder List with Chats */}
        <div className="flex-1 overflow-y-auto px-2">
          {loading || authLoading || !isDataLoaded ? <div className="text-center py-8 text-muted-foreground text-sm">
              Cargando conversaciones...
            </div> : <>
              {/* Pinned Chats Section */}
              <PinnedChatsSection chats={chats} plan={profile?.plan || 'free'} folders={folders} onEditChat={setEditingChat} onDeleteChat={setDeletingChat} onMoveToFolder={(chat, folderId) => {
            // Import and use assignChatToFolder
            import('@/services/folderService').then(({
              assignChatToFolder
            }) => {
              assignChatToFolder(chat.id, folderId).then(loadData);
            });
          }} onTogglePin={handleTogglePin} />

              {/* Folder List */}
              <FolderList folders={folders} chats={filteredChats} plan={profile?.plan || 'free'} selectedFolderId={selectedFolderId} onCreateFolder={() => setFolderDialogOpen(true)} onEditFolder={setEditingFolder} onDeleteFolder={setDeletingFolder} onEditChat={setEditingChat} onDeleteChat={setDeletingChat} onTogglePin={handleTogglePin} onRefresh={loadData} />
            </>}
        </div>

        {/* Document Management Section - Only for funcionarios */}
        {mode === 'funcionarios' && variant !== 'oposiciones' && <div className="px-4 pt-4 space-y-1">
            <Link data-tour="jurisprudencia" to="/app/funcionarios/jurisprudencia" className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg transition-colors font-medium text-primary bg-primary/5 border border-primary/10">
              <Scale className="h-4 w-4" />
              <span>Buscador Resoluciones</span>
            </Link>
            <Link data-tour="documents" to="/app/documents" className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg transition-colors">
              <FileText className="h-4 w-4" />
              <span>Mis Documentos</span>
            </Link>
            <Link to="/app/saved" className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg transition-colors">
              <Star className="h-4 w-4" />
              <span>Guardados</span>
            </Link>
            <Link data-tour="redactor" to="/app/redactor" className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg transition-colors">
              <PenTool className="h-4 w-4" />
              <span>Modo Redactor</span>
            </Link>
          </div>}

        {/* Admin Button - Solo visible para admins */}
        {(profile?.role === 'admin' || profile?.role === 'super_admin') && <div className="px-4 pt-3">
            <Link to="/admin/dashboard" className="flex items-center gap-3 px-4 py-2.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors w-full font-medium">
              <LayoutDashboard className="h-5 w-5" />
              <span>Panel Admin</span>
            </Link>
          </div>}

        {/* Deadline Calendar Button - Only for funcionarios */}
        {mode === 'funcionarios' && variant !== 'oposiciones' && <div className="px-4 pt-3" data-tour="deadlines">
            <DeadlineTriggerButton />
          </div>}

        {/* Contact Button */}
        <div className="px-4 pt-2">
          <Link to={variant === 'oposiciones' || mode !== 'funcionarios' ? '/app/oposiciones/contacto' : '/contact'} className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg transition-colors">
            <HelpCircle className="h-4 w-4" />
            <span>Ayuda y contacto</span>
          </Link>
        </div>

        {/* Mode Switcher for Betatesters - hide in oposiciones sidebar since it's in the input bar */}
        {mode === 'funcionarios' && variant !== 'oposiciones' && <ModeSwitcher />}

        {/* Current Plan & Upgrade */}
        <div className="p-4 border-t border-sidebar-border space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Tu plan actual</span>
            <PlanBadge plan={profile?.plan || 'free'} />
          </div>
          {(variant === 'oposiciones' || mode !== 'funcionarios' || profile?.plan === 'free' || !profile?.plan) && <UpgradePlanButton variant={variant === 'oposiciones' || mode !== 'funcionarios' ? 'oposiciones' : 'funcionarios'} />}
        </div>

        {/* User Menu - hide in oposiciones variant since it's in the input bar */}
        {mode === 'funcionarios' && variant !== 'oposiciones' && <div className="p-4 border-t border-sidebar-border">
            <UserMenu onOpenSettings={() => setSettingsOpen(true)} />
          </div>}
      </aside>

      <SettingsModal open={settingsOpen} onOpenChange={setSettingsOpen} />
      
      {/* Chat Dialogs */}
      <EditTitleDialog open={!!editingChat} currentTitle={editingChat?.title || ''} onOpenChange={(open) => !open && setEditingChat(null)} onSave={handleEditChat} />
      
      <DeleteChatDialog open={!!deletingChat} onOpenChange={(open) => !open && setDeletingChat(null)} onConfirm={handleDeleteChat} />

      {/* Folder Dialogs */}
      <FolderDialog open={folderDialogOpen || !!editingFolder} onOpenChange={(open) => {
      if (!open) {
        setFolderDialogOpen(false);
        setEditingFolder(null);
      }
    }} folder={editingFolder} onSave={editingFolder ? handleEditFolder : handleCreateFolder} />

      <DeleteFolderDialog open={!!deletingFolder} onOpenChange={(open) => !open && setDeletingFolder(null)} folder={deletingFolder} onConfirm={handleDeleteFolder} />
    </>;
}